/**
 * Timezone-aware date formatting utilities.
 *
 * All functions accept ISO 8601 strings and an IANA timezone identifier.
 * They use `Intl.DateTimeFormat` — no external date library required.
 *
 * Usage:
 *   const tz = useUserTimezone();
 *   const { language } = useLanguage();
 *   formatDate("2026-06-15T08:00:00Z", tz, language)       // "15 Jun 2026"
 *   formatDateTime("2026-06-15T08:00:00Z", tz, language)   // "15 Jun 2026, 10:00"
 *   formatAuditTimestamp("2026-06-15T08:00:00Z", tz)       // "2026-06-15 10:00 CEST (08:00 UTC)"
 */

function safeTimezone(timezone: string): string {
  try {
    new Intl.DateTimeFormat('en', { timeZone: timezone }).format(new Date());
    return timezone;
  } catch {
    return 'UTC';
  }
}

/**
 * Format a UTC ISO string as a date in the given IANA timezone.
 * Returns the raw string if it cannot be parsed as a date.
 * Falls back to UTC if the timezone is unrecognised.
 * Example output: "15 Jun 2026" (locale "en") or "15 jun. 2026" (locale "sv")
 */
export function formatDate(isoString: string, timezone: string, locale = 'en'): string {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return isoString;
  return new Intl.DateTimeFormat(locale, {
    timeZone: safeTimezone(timezone),
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/**
 * Format a UTC ISO string as a date + time in the given IANA timezone.
 * Returns the raw string if it cannot be parsed as a date.
 * Falls back to UTC if the timezone is unrecognised.
 * Example output: "15 Jun 2026, 10:00"
 */
export function formatDateTime(isoString: string, timezone: string, locale = 'en'): string {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return isoString;
  return new Intl.DateTimeFormat(locale, {
    timeZone: safeTimezone(timezone),
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date);
}

/**
 * Format a UTC ISO string for audit/legal display.
 * Shows the local time in the user's timezone with abbreviated TZ name,
 * and the UTC equivalent in parentheses.
 * Returns the raw string if it cannot be parsed as a date.
 * Falls back to UTC if the timezone is unrecognised.
 * Example output: "2026-06-15 10:00 CEST (08:00 UTC)"
 */
export function formatAuditTimestamp(isoString: string, timezone: string): string {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return isoString;

  const tz = safeTimezone(timezone);

  const localParts = new Intl.DateTimeFormat('en-u-nu-latn', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  const tzAbbr = new Intl.DateTimeFormat('en-u-nu-latn', {
    timeZone: tz,
    timeZoneName: 'short',
  }).formatToParts(date).find((p) => p.type === 'timeZoneName')?.value ?? tz;

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    localParts.find((p) => p.type === type)?.value ?? '';

  const utcParts = new Intl.DateTimeFormat('en-u-nu-latn', {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  const utcHour = utcParts.find((p) => p.type === 'hour')?.value ?? '';
  const utcMinute = utcParts.find((p) => p.type === 'minute')?.value ?? '';

  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')} ${tzAbbr} (${utcHour}:${utcMinute} UTC)`;
}
